// ACTA_AGENT_LLM_PLANNER_CLIENT_V5
// ACTA_AGENT_TWO_STAGE_PLANNER_V6
//
// Ce client obtient et valide un plan.
// Il n'exécute aucune action métier.
//
// Compatibilité V5 : buildAgentPlannerRequest a été remplacé
// pour le runtime par classification + extraction.

import {
  agent as agentApi,
} from "@/api";

import {
  availableAgentToolContracts,
} from "@/agent/actionRegistry";

import {
  buildAgentActionClassificationRequest,
  buildAgentActionExtractionRequest,
} from "@/agent/llmPlannerSchema";

import {
  validateAgentStructuredPlan,
} from "@/agent/actionPlanner";


function cleanCommandValue(
  command
) {
  const clean =
    String(
      command || ""
    ).trim();

  if (!clean) {
    throw new Error(
      "La demande Agent est vide."
    );
  }

  return clean;
}


function availableContracts({
  user,
  hasFeature,
  allowedActionIds = null,
}) {
  const allowed =
    Array.isArray(
      allowedActionIds
    )
      ? new Set(
          allowedActionIds
        )
      : null;

  const contracts =
    availableAgentToolContracts({
      user,
      hasFeature,
    })
      .filter(
        (contract) =>
          contract.plannerReady
      )
      .filter(
        (contract) =>
          !allowed
          || allowed.has(
            contract.id
          )
      );

  if (!contracts.length) {
    throw new Error(
      "Aucune action Agent disponible."
    );
  }

  return contracts;
}


function singlePlanStep(
  plan,
  label
) {
  const actions =
    plan?.actions;

  if (
    !Array.isArray(actions)
    || actions.length !== 1
    || !actions[0]
    || typeof actions[0].id
      !== "string"
    || !actions[0].id.trim()
    || typeof actions[0].params
      !== "object"
    || actions[0].params === null
    || Array.isArray(
      actions[0].params
    )
  ) {
    throw new Error(
      `${label} Agent invalide.`
    );
  }

  return actions[0];
}


export function buildAgentClassificationEnvelope({
  command,
  user,
  hasFeature,
  allowedActionIds = null,
  nowIso =
    new Date().toISOString(),
}) {
  const cleanCommand =
    cleanCommandValue(
      command
    );

  const contracts =
    availableContracts({
      user,
      hasFeature,
      allowedActionIds,
    });

  return {
    command:
      cleanCommand,
    contracts,
    plannerRequest:
      buildAgentActionClassificationRequest({
        contracts,
        command:
          cleanCommand,
        model:
          "acta-planner",
        nowIso,
      }),
    availableActionIds:
      contracts.map(
        (contract) =>
          contract.id
      ),
  };
}


export function buildAgentPlanningEnvelope(
  options
) {
  return buildAgentClassificationEnvelope(
    options
  );
}


export function buildAgentExtractionEnvelope({
  command,
  contract,
  nowIso =
    new Date().toISOString(),
}) {
  const cleanCommand =
    cleanCommandValue(
      command
    );

  if (
    !contract
    || !contract.plannerReady
  ) {
    throw new Error(
      "Action Agent invalide pour l'extraction."
    );
  }

  return {
    command:
      cleanCommand,
    plannerRequest:
      buildAgentActionExtractionRequest({
        contract,
        command:
          cleanCommand,
        model:
          "acta-planner",
        nowIso,
      }),
    availableActionIds: [
      contract.id,
    ],
  };
}


export async function planAgentCommand({
  command,
  user,
  hasFeature,
  allowedActionIds = null,
  nowIso,
}) {
  const effectiveNow =
    nowIso
    || new Date()
      .toISOString();

  const classificationEnvelope =
    buildAgentClassificationEnvelope({
      command,
      user,
      hasFeature,
      allowedActionIds,
      nowIso:
        effectiveNow,
    });

  const classificationResponse =
    await agentApi.plan(
      {
        command:
          classificationEnvelope
            .command,
        plannerRequest:
          classificationEnvelope
            .plannerRequest,
        availableActionIds:
          classificationEnvelope
            .availableActionIds,
      }
    );

  const classifiedStep =
    singlePlanStep(
      classificationResponse
        ?.plan,
      "Classification"
    );

  const byId =
    new Map(
      classificationEnvelope
        .contracts
        .map(
          (contract) => [
            contract.id,
            contract,
          ]
        )
    );

  const contract =
    byId.get(
      classifiedStep.id
    );

  if (!contract) {
    throw new Error(
      "Le classifieur a choisi une action indisponible."
    );
  }

  const extractionEnvelope =
    buildAgentExtractionEnvelope({
      command:
        classificationEnvelope
          .command,
      contract,
      nowIso:
        effectiveNow,
    });

  const extractionResponse =
    await agentApi.plan(
      extractionEnvelope
    );

  const extractedStep =
    singlePlanStep(
      extractionResponse
        ?.plan,
      "Extraction"
    );

  if (
    extractedStep.id
    !== contract.id
  ) {
    throw new Error(
      "L'extracteur a tenté de changer l'action."
    );
  }

  const plan =
    extractionResponse
      ?.plan;

  const validation =
    validateAgentStructuredPlan({
      plan,
      user,
      hasFeature,
    });

  if (!validation.valid) {
    throw new Error(
      validation.error
      || "Plan Agent invalide."
    );
  }

  return {
    ...validation,
    provider:
      extractionResponse
        ?.provider
      || classificationResponse
        ?.provider
      || "",
    model:
      extractionResponse
        ?.model
      || classificationResponse
        ?.model
      || "",
    classifiedActionId:
      contract.id,
    rawClassificationPlan:
      classificationResponse
        ?.plan,
    rawPlan:
      plan,
  };
}
